import * as React from 'react';
import { DialerPhoneCallStatus, useTelephony } from '../../../contexts/telephony';
import { useQueue } from '../../../hooks/queue';
import { useTysonCoaching } from '../../contexts/TysonCoachingContext';
import { useRealCallCoaching } from '../../hooks/useRealCallCoaching';
import { useRealCallCoachingSession } from '../../hooks/useRealCallCoachingSession';
/**
 * Bridge component that connects real Twilio calls to the coaching system.
 * Renders nothing - purely handles side effects.
 */
export const RealCallCoachingBridge: React.FC = () => {
	const { phoneCall, phoneCallStatus, dialingContactId } = useTelephony();
	const queue = useQueue();
	const coaching = useTysonCoaching();

	// Extract stable references to avoid dependency issues
	const registerFlushCallback = coaching.registerFlushCallback;
	const setLiveUtterances = coaching.setLiveUtterances;
	const setCallEndedPendingSummary = coaching.setCallEndedPendingSummary;
	const setIsCallConnected = coaching.setIsCallConnected;
	const clearPendingTranscriptData = coaching.clearPendingTranscriptData;
	const sendNewCallStartedMessage = coaching.sendNewCallStartedMessage;

	const prevStatusRef = React.useRef<DialerPhoneCallStatus>(phoneCallStatus);
	// Refs to avoid re-firing status-transition effects when function references change
	const sendNewCallStartedMessageRef = React.useRef(sendNewCallStartedMessage);
	sendNewCallStartedMessageRef.current = sendNewCallStartedMessage;
	const clearPendingTranscriptDataRef = React.useRef(clearPendingTranscriptData);
	clearPendingTranscriptDataRef.current = clearPendingTranscriptData;

	const { isReady } = useRealCallCoachingSession({
		companyId: queue?.companyId || phoneCall?.companyId || null,
		contactId: phoneCall?.contactIds?.[0] || null,
		leadServedId: queue?.leadServedId,
		phoneCallId: phoneCall?.id,
	});

	// Bridge transcription events to coaching when call is connected
	const isCallActive = phoneCallStatus === DialerPhoneCallStatus.Connected;

	const { liveUtterances, flushAndGetEntries, clearTranscript } = useRealCallCoaching({
		phoneCallId: phoneCall?.id || null,
		enabled: isCallActive && isReady,
	});

	// Register flush callback with context so both "Get Suggestion" and "Get Summary" can use it.
	// Only unregister on unmount — the callback must remain available after the call ends so
	// requestPostCallSummary can flush remaining transcript when the user clicks "Get Summary".
	React.useEffect(() => {
		if (isReady) {
			registerFlushCallback(flushAndGetEntries);
		}

		return () => {
			registerFlushCallback(null);
		};
	}, [isReady, flushAndGetEntries, registerFlushCallback]);

	// Sync live utterances to context
	React.useEffect(() => {
		setLiveUtterances(liveUtterances);
	}, [liveUtterances, setLiveUtterances]);

	// Flag summary available when call ends.
	// No flush here — remaining transcript is flushed lazily by requestPostCallSummary
	// when the user clicks "Get Summary", so the transcript and summary travel together.
	React.useEffect(() => {
		const wasConnected = prevStatusRef.current === DialerPhoneCallStatus.Connected;
		const isNowEnded = phoneCallStatus === DialerPhoneCallStatus.NoCall;

		if (wasConnected && isNowEnded && isReady) {
			setCallEndedPendingSummary(true);
		}

		prevStatusRef.current = phoneCallStatus;
	}, [phoneCallStatus, isReady, setCallEndedPendingSummary]);

	// Reset state when new call starts — use refs for callbacks to prevent re-firing
	React.useEffect(() => {
		if (phoneCallStatus === DialerPhoneCallStatus.Dialing) {
			const contact = dialingContactId ? queue?.lead?.contacts?.find(c => c.id === dialingContactId) : null;

			const contactName = contact
				? `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'Unknown Contact'
				: 'Unknown Contact';
			setCallEndedPendingSummary(false);
			clearPendingTranscriptDataRef.current();
			clearTranscript(); // also reset the hook-level completedUtterancesRef
			sendNewCallStartedMessageRef.current(contactName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCallStatus, setCallEndedPendingSummary]);

	// Sync call connected state to context
	React.useEffect(() => {
		setIsCallConnected(phoneCallStatus === DialerPhoneCallStatus.Connected);
	}, [phoneCallStatus, setIsCallConnected]);

	return null;
};
